// Performance Score Donut Chart - Replicating Sentry UI

import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  WEB_VITAL_COLORS, 
  getStatusFromScore, 
  STATUS_CONFIG,
  type MetricScores 
} from '@/types/performance';

interface PerformanceScoreDonutProps {
  score: number;
  metricScores: MetricScores;
  isLoading?: boolean;
}

export function PerformanceScoreDonut({ score, metricScores, isLoading }: PerformanceScoreDonutProps) {
  const status = getStatusFromScore(score);
  const statusConfig = STATUS_CONFIG[status];

  // Create data for the donut chart
  const donutData = [
    { name: 'LCP', value: metricScores.lcp, color: WEB_VITAL_COLORS.lcp },
    { name: 'FCP', value: metricScores.fcp, color: WEB_VITAL_COLORS.fcp },
    { name: 'CLS', value: metricScores.cls, color: WEB_VITAL_COLORS.cls },
    { name: 'INP', value: metricScores.inp, color: WEB_VITAL_COLORS.inp },
    { name: 'TTFB', value: metricScores.ttfb, color: WEB_VITAL_COLORS.ttfb },
  ];

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Performance Score</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[200px]">
          <div className="animate-pulse bg-muted rounded-full w-32 h-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Performance Score</CardTitle>
      </CardHeader>
      <CardContent className="relative">
        <div className="h-[200px] relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={donutData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={75}
                paddingAngle={2}
                dataKey="value"
              >
                {donutData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          
          {/* Center score */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-4xl font-bold">{score}</span>
            <span className={`text-xs px-2 py-0.5 rounded ${statusConfig.bgColor} ${statusConfig.textColor}`}>
              {statusConfig.label}
            </span>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-3 mt-4">
          {donutData.map((item) => (
            <div key={item.name} className="flex items-center gap-1.5">
              <div 
                className="w-2.5 h-2.5 rounded-full" 
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs text-muted-foreground">{item.name}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
